import { useEffect, useState } from "react";
import type { AppProps } from "next/app";
import { NextSeo } from "next-seo";
import { AuthRedirectGuard } from "domains/auth/components/AuthRedirectGuard";
import { BackgroundTaskProvider } from "domains/background-tasks/contexts/BackgroundTaskProvider";
import { CanvasLockProvider } from "domains/canvas/hooks/useCanvasLock";
import { ClipboardProvider } from "domains/commons/contexts/ClipboardProvider";
import { HotkeysProvider } from "domains/commons/contexts/HotkeysProvider";
import { DragDropBackend } from "domains/commons/dnd";
import { TourGuideProvider } from "domains/guide/contexts/TourGuideProvider";
import { SectionsAllProvider } from "domains/inference/contexts/SectionsProvider";
import { IntlProvider } from "domains/messages/components/IntlProvider";
import { CreateNewModelModalProvider } from "domains/models/contexts/CreateNewModelModalProvider";
import { ChatProvider } from "domains/navigation/contexts/ChatProvider";
import { NavBarProvider } from "domains/navigation/contexts/NavBarProvider";
import useRouter from "domains/navigation/hooks/useRouter";
import { SearchProvider } from "domains/search/contexts/SearchProvider";
import SEOKeywords from "domains/seo/keywords.json";
import { PlanProvider } from "domains/teams/contexts/PlanProvider";
import { TeamProvider } from "domains/teams/contexts/TeamProvider";
import theme from "domains/theme";
import Layout from "domains/ui/components/Layout";
import OnboardingForm from "domains/user/components/OnboardingForm";
import PolicyAcceptance from "domains/user/components/PolicyAcceptance";
import { UserProvider } from "domains/user/contexts/UserProvider";
import { WebsocketProvider } from "domains/websocket/contexts/Websocket";
import POSTHOG_ID from "infra/analytics/constants/PosthogId";
import ErrorBoundary from "infra/errors/ErrorBoundary";
import store from "infra/store/store";
import posthog from "posthog-js";
import { DndProvider } from "react-dnd";
import { Provider as ReduxProvider } from "react-redux";

import { CSSReset } from "@chakra-ui/css-reset";
import { ChakraProvider } from "@chakra-ui/react";
import { ClerkProvider } from "@clerk/nextjs";
import { SpeedInsights } from "@vercel/speed-insights/next";

import "domains/theme/globals.css";
// We need to import this for the canvas, if we place it in another file than _app it will break the style once build
import "domains/canvas/css/blueprint.css";
import "domains/ui/components/Markdown/Viewer/css/markdown-viewer.css";
import "domains/ui/components/Datepicker/styles.css";
// eslint-disable-next-line no-restricted-imports
import "../../public/lib/flag-icons/css/styles.css";

export default function App({ Component, pageProps }: AppProps) {
  const router = useRouter();
  const [lastBuildIdFetch, setLastBuildIdFetch] = useState<
    number | undefined
  >();

  useEffect(() => {
    // PostHog-style console banner
    console.log(
      `%c Scenario Inc. %c Take complete control of your creative AI workflows%c Commit or Deployment: ${
        process.env.VERCEL_DEPLOYMENT_ID ?? "unknown"
      } %c  %c Use our API: https://docs.scenario.com/`,
      // Scenario Inc. gradient background, white text
      "background: #fff; color: #fff; font-weight: bold; background: linear-gradient(90deg,#FFB700,#FF6A00,#6A00FF,#007AFF); padding: 8px 4px; border-radius: 4px;",
      // Italic subtitle, white text, transparent background
      "color: #fff; font-style: italic; padding: 4px 8px 4px 0; background: transparent;",
      // Commit badge: orange background, white text
      "background: #DC3400; color: #fff; border-radius: 4px; padding: 2px 8px;",
      // Transparent background
      "background: transparent;",
      // API callout: yellow background, blue link, black text
      "background: #0088CC; color: #fff; border-radius: 4px; padding: 2px 8px;"
    );
  }, []);

  useEffect(() => {
    if (lastBuildIdFetch && Date.now() - lastBuildIdFetch < 1_000 * 60 * 5) {
      return;
    }
    if (process.env.NODE_ENV !== "production") {
      return;
    }
    void fetch("/api/build-id")
      .then((res) => res.json())
      .then((data) => {
        const clientBuildId = process.env.VERCEL_DEPLOYMENT_ID;
        const serverBuildId = data.buildId;
        if (clientBuildId !== serverBuildId) {
          window.location.reload();
        }
      });
    setLastBuildIdFetch(Date.now());
  }, [lastBuildIdFetch, router.pathname]);

  useEffect(() => {
    if (POSTHOG_ID === undefined || POSTHOG_ID === "") {
      return;
    }
    if (process.env.NODE_ENV === "production") {
      posthog.init(POSTHOG_ID, {
        api_host: process.env.NEXT_PUBLIC_POSTHOG_PROXY,
        autocapture: false,
        enable_heatmaps: true,
        person_profiles: "identified_only",
        defaults: "2025-05-24", // defaults throw a TS error
      } as any);
    } else {
      posthog.init(POSTHOG_ID, {
        api_host: "https://us.i.posthog.com",
        autocapture: false,
        enable_heatmaps: true,
        person_profiles: "identified_only",
        disable_session_recording: true,
        defaults: "2025-05-24", // defaults throw a TS error
      } as any);
    }
  }, []);

  useEffect(() => {
    if (navigator.userAgent.indexOf("Mac") === -1) {
      document.body.setAttribute("class", "custom-scrollbar");
    }
  }, []);

  return (
    <ChakraProvider theme={theme}>
      <IntlProvider>
        <ClerkProvider>
          <AuthRedirectGuard>
            <NextSeo
              titleTemplate="%s | Scenario"
              description="Create production-ready game assets and marketing visuals with Scenario — the AI platform for creators, studios, and brands. Use powerful prebuilt models or train your own."
              additionalMetaTags={[
                {
                  name: "keywords",
                  content: SEOKeywords.join(", "),
                },
              ]}
              canonical="https://app.scenario.com"
              openGraph={{
                title:
                  "Scenario - AI-powered platform for creators, game studios, and marketers",
                description:
                  "Create production-ready game assets and marketing visuals with Scenario — the AI platform for creators, studios, and brands. Use powerful prebuilt models or train your own.",
                images: [
                  {
                    url: "/og.png",
                    height: 1_068,
                    width: 2_048,
                  },
                ],
              }}
              twitter={{
                cardType: "summary_large_image",
                handle: "@scenario_gg",
                site: "https://scenario.com",
              }}
            />
            <ReduxProvider store={store}>
              <ClipboardProvider>
                <HotkeysProvider>
                  <UserProvider>
                    <TeamProvider>
                      <SearchProvider>
                        <NavBarProvider>
                          <CreateNewModelModalProvider>
                            <CSSReset />
                            <ErrorBoundary>
                              <WebsocketProvider>
                                <PlanProvider>
                                  <ChatProvider>
                                    <BackgroundTaskProvider>
                                      <Layout>
                                        <CanvasLockProvider>
                                          <TourGuideProvider>
                                            <DndProvider
                                              backend={DragDropBackend}
                                            >
                                              <SectionsAllProvider>
                                                <Component {...pageProps} />
                                                <PolicyAcceptance />
                                                <OnboardingForm />
                                                <SpeedInsights />
                                              </SectionsAllProvider>
                                            </DndProvider>
                                          </TourGuideProvider>
                                        </CanvasLockProvider>
                                      </Layout>
                                    </BackgroundTaskProvider>
                                  </ChatProvider>
                                </PlanProvider>
                              </WebsocketProvider>
                            </ErrorBoundary>
                          </CreateNewModelModalProvider>
                        </NavBarProvider>
                      </SearchProvider>
                    </TeamProvider>
                  </UserProvider>
                </HotkeysProvider>
              </ClipboardProvider>
            </ReduxProvider>
          </AuthRedirectGuard>
        </ClerkProvider>
      </IntlProvider>
    </ChakraProvider>
  );
}
